import type { ReactElement } from "react"
import { Fragment } from "react"

import { useQuery } from "react-query"
import { useSelector } from "react-redux"

import { Grid } from "@mui/material"
import type { AxiosError, AxiosResponse } from "axios"

import { KonanClassificationAlert } from "../../../components/status/KonanClassificationAlert"
import { KonanAPI } from "../../../services/KonanAPI"
import type { RootState } from "../../../store/ReduxStore"
import type { ListClassificationModelOutputs } from "../../../types/generated/api/ListClassificationModelOutputs"

/**
 * Container component for handling model configuration alerts
 * Displays alerts when a non-generic project requires configuration
 * @returns {ReactElement}
 */
export function ConfigurationAlert(): ReactElement {
  // Get current location for path checking
  const currentProject = useSelector((state: RootState) => state.project.currentProject)
  const currentModel = useSelector((state: RootState) => state.models.currentModel)

  // Fetch active configuration for the current model, disable refetching in integration screen
  const { data: activeConfiguration, isLoading: isLoadingActiveConfiguration } = useQuery<
    AxiosResponse<ListClassificationModelOutputs>,
    AxiosError
  >(["activeConfig", currentModel], () => KonanAPI.fetchActiveModelConfiguration(currentModel?.uuid as string), {
    // Only enable for non-generic projects with valid model
    enabled: !!currentModel && !!currentProject && currentProject.type !== "generic",
    retry: false,
    refetchOnWindowFocus: false,
  })

  const shouldDisplayAlert =
    currentProject &&
    currentProject?.type !== "generic" &&
    !isLoadingActiveConfiguration &&
    !activeConfiguration &&
    currentModel

  return (
    <Fragment>
      {shouldDisplayAlert && (
        <Grid pb={2} xs={12}>
          <KonanClassificationAlert id={currentProject?.uuid} modelId={currentModel?.uuid} />
        </Grid>
      )}
    </Fragment>
  )
}
